import {
  LEARNING_OPERATE_ROLES,
  ONBOARDING_DOCUMENT_VERIFY_ROLES,
} from '@/lib/workforce/people-content-cohorts';
/**
 * People/Workforce Phase 1A — capability catalogue mirror + pure helpers.
 *
 * Source contract: docs/source-of-truth/people-workforce-phase1a-contract.md
 * SQL owner: supabase/migrations/20270401000000_people_workforce_phase1a_foundation.sql
 *
 * Wave 1A establishes side-by-side parity only. Route arrays and global
 * capability maps are not switched here (Wave 1B/2).
 */

export const WORKFORCE_PHASE1A_CAPABILITIES = [
  'workforce.people.list',
  'workforce.people.view',
  'workforce.engagement.manage',
  'workforce.onboarding.manage',
  'workforce.documents.verify',
  'workforce.screening.verify',
  'workforce.credentials.verify',
  'workforce.learning.assign',
  'workforce.learning.verify_external',
  'workforce.policy.govern',
  'workforce.policy.assign',
  'workforce.conduct.manage',
  'workforce.performance.manage',
  'workforce.supervision.perform',
  'workforce.offboarding.manage',
  'workforce.access.terminate',
  'workforce.evidence.export',
  'workforce.requirements.configure',
] as const;

export type WorkforcePhase1aCapabilityId = (typeof WORKFORCE_PHASE1A_CAPABILITIES)[number];

export type CapabilityMeta = {
  capabilityId: WorkforcePhase1aCapabilityId;
  requiresStaffIdentity: boolean;
  isProfessionalDecision: boolean;
  allowsAdminFallback: boolean;
  professionalScopeRequirement: string | null;
};

/** Mirrors workforce_capabilities seed rows. */
export const WORKFORCE_CAPABILITY_META: Record<WorkforcePhase1aCapabilityId, CapabilityMeta> = {
  'workforce.people.list': {
    capabilityId: 'workforce.people.list',
    requiresStaffIdentity: false,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.people.view': {
    capabilityId: 'workforce.people.view',
    requiresStaffIdentity: false,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.engagement.manage': {
    capabilityId: 'workforce.engagement.manage',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.onboarding.manage': {
    capabilityId: 'workforce.onboarding.manage',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.documents.verify': {
    capabilityId: 'workforce.documents.verify',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.screening.verify': {
    capabilityId: 'workforce.screening.verify',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.credentials.verify': {
    capabilityId: 'workforce.credentials.verify',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.learning.assign': {
    capabilityId: 'workforce.learning.assign',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.learning.verify_external': {
    capabilityId: 'workforce.learning.verify_external',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.policy.govern': {
    capabilityId: 'workforce.policy.govern',
    requiresStaffIdentity: false,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.policy.assign': {
    capabilityId: 'workforce.policy.assign',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.conduct.manage': {
    capabilityId: 'workforce.conduct.manage',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.performance.manage': {
    capabilityId: 'workforce.performance.manage',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.supervision.perform': {
    capabilityId: 'workforce.supervision.perform',
    requiresStaffIdentity: true,
    isProfessionalDecision: true,
    allowsAdminFallback: false,
    professionalScopeRequirement: 'CLINICAL_SUPERVISOR',
  },
  'workforce.offboarding.manage': {
    capabilityId: 'workforce.offboarding.manage',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.access.terminate': {
    capabilityId: 'workforce.access.terminate',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.evidence.export': {
    capabilityId: 'workforce.evidence.export',
    requiresStaffIdentity: true,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
  'workforce.requirements.configure': {
    capabilityId: 'workforce.requirements.configure',
    requiresStaffIdentity: false,
    isProfessionalDecision: false,
    allowsAdminFallback: true,
    professionalScopeRequirement: null,
  },
};

/**
 * Effective role grants — must match workforce_capability_role_grants seed.
 * Do not broaden access beyond currently intended HR/workforce cohorts.
 */
export const WORKFORCE_CAPABILITY_ROLE_GRANTS: Record<
  WorkforcePhase1aCapabilityId,
  readonly string[]
> = {
  'workforce.people.list': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'OWNER',
    'CEO',
    'OPERATIONS_DIRECTOR',
    'HR_DIRECTOR',
    'HR_OFFICER',
    'FACILITY_MANAGER',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGION_COMPLIANCE_OFFICER',
    'CLINICAL_LEAD',
    'TEAM_LEADER',
    // List-only access; never spread this cohort into other workforce readers.
    'REGISTERED_NURSE',
    'COMPLIANCE_OFFICER',
    'QUALITY_OFFICER',
    'FINANCE_OFFICER',
  ],
  'workforce.people.view': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'OWNER',
    'CEO',
    'OPERATIONS_DIRECTOR',
    'HR_DIRECTOR',
    'HR_OFFICER',
    'FACILITY_MANAGER',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGION_COMPLIANCE_OFFICER',
    'CLINICAL_LEAD',
    'TEAM_LEADER',
  ],
  'workforce.engagement.manage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'OWNER',
    'CEO',
    'OPERATIONS_DIRECTOR',
    'HR_DIRECTOR',
    'HR_OFFICER',
    'FACILITY_MANAGER',
    'REGION_MANAGER',
    'COORDINATOR',
  ],
  'workforce.onboarding.manage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'HR_OFFICER',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'HR_DIRECTOR',
  ],
  'workforce.documents.verify': ONBOARDING_DOCUMENT_VERIFY_ROLES,
  'workforce.screening.verify': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'TEAM_LEADER',
  ],
  'workforce.credentials.verify': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
  ],
  'workforce.learning.assign': LEARNING_OPERATE_ROLES.filter(
    (role) => role !== 'HR_DIRECTOR' && role !== 'HR_OFFICER',
  ),
  'workforce.learning.verify_external': LEARNING_OPERATE_ROLES.filter(
    (role) => role !== 'HR_DIRECTOR' && role !== 'HR_OFFICER',
  ),
  'workforce.policy.govern': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'QUALITY_OFFICER', 'HR_DIRECTOR'],
  'workforce.policy.assign': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  'workforce.conduct.manage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'HR_DIRECTOR',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ],
  // Matches the canonical Development performance cohort — not broadened.
  'workforce.performance.manage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
  ],
  // Professional performer cohort — admin/recording permission is not performer scope.
  'workforce.supervision.perform': ['CLINICAL_LEAD', 'REGISTERED_NURSE', 'NURSE_PRACTITIONER'],
  'workforce.offboarding.manage': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'FINANCE_OFFICER',
  ],
  'workforce.access.terminate': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER'],
  'workforce.evidence.export': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'HR_DIRECTOR',
    'HR_OFFICER',
  ],
  'workforce.requirements.configure': ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
};

export type RecipientCandidate = {
  userId: string;
  staffId?: string | null;
  role: string;
  isActiveUser: boolean;
  isActiveStaff: boolean;
  facilityIds?: readonly string[];
  /** Target workers linked by an active effective professional assignment. */
  assignedTargetStaffIds?: readonly string[];
};

export type CapabilityResolutionInput = {
  capabilityId: WorkforcePhase1aCapabilityId | string;
  actor: RecipientCandidate;
  candidates: readonly RecipientCandidate[];
  targetStaffId?: string | null;
  accountableManagerStaffId?: string | null;
  facilityId?: string | null;
};

export type CapabilityResolutionResult = {
  state: 'RESOLVED' | 'UNOWNED';
  reasonCode:
    | 'NO_QUALIFIED_PROFESSIONAL'
    | 'INACTIVE_MANAGER'
    | 'OUT_OF_SCOPE'
    | 'NO_ACTIVE_STAFF_IDENTITY'
    | 'TARGET_STAFF_REQUIRED'
    | 'UNKNOWN_CAPABILITY'
    | null;
  primaryRecipient: RecipientCandidate | null;
  fallbackRecipient: RecipientCandidate | null;
  note?: string;
};

function roleHasGrant(capabilityId: string, role: string): boolean {
  const grants = WORKFORCE_CAPABILITY_ROLE_GRANTS[capabilityId as WorkforcePhase1aCapabilityId];
  if (!grants) return false;
  return grants.includes(role);
}

/**
 * Pure recipient resolver mirroring resolve_workforce_capability_recipient semantics.
 * Professional decisions never make tenant admin the performer.
 */
export function resolveWorkforceCapabilityRecipient(
  input: CapabilityResolutionInput,
): CapabilityResolutionResult {
  const meta = WORKFORCE_CAPABILITY_META[input.capabilityId as WorkforcePhase1aCapabilityId];
  if (!meta) {
    return {
      state: 'UNOWNED',
      reasonCode: 'UNKNOWN_CAPABILITY',
      primaryRecipient: null,
      fallbackRecipient: null,
    };
  }

  if (input.facilityId) {
    const inScope =
      input.actor.role === 'SYSTEM_ADMIN' ||
      input.actor.role === 'TENANT_ADMIN' ||
      input.actor.role === 'REGION_MANAGER' ||
      (input.actor.facilityIds ?? []).includes(input.facilityId);
    if (!inScope && meta.requiresStaffIdentity) {
      return {
        state: 'UNOWNED',
        reasonCode: 'OUT_OF_SCOPE',
        primaryRecipient: null,
        fallbackRecipient: null,
      };
    }
  }

  const grantHolders = input.candidates.filter((c) => {
    if (!c.isActiveUser) return false;
    if (!roleHasGrant(input.capabilityId, c.role)) return false;
    if (input.facilityId) {
      if (
        c.role !== 'SYSTEM_ADMIN' &&
        c.role !== 'TENANT_ADMIN' &&
        c.role !== 'REGION_MANAGER' &&
        !(c.facilityIds ?? []).includes(input.facilityId)
      ) {
        return false;
      }
    }
    return true;
  });

  if (meta.isProfessionalDecision) {
    const fallback = adminEscalation(input.candidates, meta);
    if (!input.targetStaffId) {
      return {
        state: 'UNOWNED',
        reasonCode: 'TARGET_STAFF_REQUIRED',
        primaryRecipient: null,
        fallbackRecipient: fallback,
        note: 'Admin escalation only; professional decision remains unowned',
      };
    }

    const professionalRoles = new Set(['CLINICAL_LEAD', 'REGISTERED_NURSE', 'NURSE_PRACTITIONER']);
    const assigned = grantHolders.filter(
      (c) =>
        c.isActiveStaff &&
        professionalRoles.has(c.role) &&
        (c.assignedTargetStaffIds ?? []).includes(input.targetStaffId as string),
    );
    const primary =
      assigned.find((candidate) => candidate.staffId === input.actor.staffId) ??
      assigned[0] ??
      null;
    if (primary) {
      return {
        state: 'RESOLVED',
        reasonCode: null,
        primaryRecipient: primary,
        fallbackRecipient: null,
      };
    }

    const hasInactiveAssignment = input.candidates.some(
      (candidate) =>
        professionalRoles.has(candidate.role) &&
        (candidate.assignedTargetStaffIds ?? []).includes(input.targetStaffId as string) &&
        (!candidate.isActiveUser || !candidate.isActiveStaff),
    );
    return {
      state: 'UNOWNED',
      reasonCode: hasInactiveAssignment ? 'INACTIVE_MANAGER' : 'NO_QUALIFIED_PROFESSIONAL',
      primaryRecipient: null,
      fallbackRecipient: fallback,
      note: 'Admin escalation only; professional decision remains unowned',
    };
  }

  if (input.accountableManagerStaffId) {
    const namedManager = input.candidates.find(
      (candidate) => candidate.staffId === input.accountableManagerStaffId,
    );
    if (namedManager && (!namedManager.isActiveUser || !namedManager.isActiveStaff)) {
      return {
        state: 'UNOWNED',
        reasonCode: 'INACTIVE_MANAGER',
        primaryRecipient: null,
        fallbackRecipient: adminEscalation(input.candidates, meta),
      };
    }
    if (
      namedManager &&
      roleHasGrant(input.capabilityId, namedManager.role) &&
      (!input.facilityId ||
        ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'REGION_MANAGER'].includes(namedManager.role) ||
        (namedManager.facilityIds ?? []).includes(input.facilityId))
    ) {
      return {
        state: 'RESOLVED',
        reasonCode: null,
        primaryRecipient: namedManager,
        fallbackRecipient: adminEscalation(input.candidates, meta),
      };
    }
  }

  const activeQualified = grantHolders.filter(
    (candidate) => !meta.requiresStaffIdentity || candidate.isActiveStaff,
  );
  const primary =
    activeQualified.find((candidate) => candidate.userId === input.actor.userId) ??
    activeQualified[0] ??
    null;
  if (primary) {
    return {
      state: 'RESOLVED',
      reasonCode: null,
      primaryRecipient: primary,
      fallbackRecipient: null,
    };
  }

  const fallback = adminEscalation(input.candidates, meta);
  if (fallback && meta.allowsAdminFallback) {
    return {
      state: 'RESOLVED',
      reasonCode: null,
      primaryRecipient: fallback,
      fallbackRecipient: null,
    };
  }
  if (meta.requiresStaffIdentity) {
    return {
      state: 'UNOWNED',
      reasonCode: 'NO_ACTIVE_STAFF_IDENTITY',
      primaryRecipient: null,
      fallbackRecipient: fallback,
    };
  }
  return {
    state: 'UNOWNED',
    reasonCode: 'NO_QUALIFIED_PROFESSIONAL',
    primaryRecipient: null,
    fallbackRecipient: fallback,
  };
}

function adminEscalation(
  candidates: readonly RecipientCandidate[],
  meta: CapabilityMeta,
): RecipientCandidate | null {
  if (!meta.allowsAdminFallback && !meta.isProfessionalDecision) return null;
  return (
    candidates.find(
      (c) => c.isActiveUser && (c.role === 'TENANT_ADMIN' || c.role === 'SYSTEM_ADMIN'),
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// Engagement bootstrap / accountability pure helpers
// ---------------------------------------------------------------------------

export type EngagementBootstrap = {
  classificationState: 'LEGACY_UNCLASSIFIED' | 'CONFIRMED';
  workerClassification:
    | 'UNCLASSIFIED'
    | 'EMPLOYEE'
    | 'CONTRACTOR'
    | 'INDEPENDENT_CONTRACTOR'
    | 'VOLUNTEER'
    | 'STUDENT'
    | 'ASSOCIATED_PROVIDER_WORKER';
  accountabilityResolution: 'RESOLVED' | 'UNABLE_TO_DETERMINE';
  inferredFromStaffRole: boolean;
  inferredFromEmploymentType: boolean;
  inferredFromIsAgency: boolean;
};

/** Existing/new staff bootstrap — never silently direct employee or compliant. */
export function bootstrapWorkerEngagement(): EngagementBootstrap {
  return {
    classificationState: 'LEGACY_UNCLASSIFIED',
    workerClassification: 'UNCLASSIFIED',
    accountabilityResolution: 'UNABLE_TO_DETERMINE',
    inferredFromStaffRole: false,
    inferredFromEmploymentType: false,
    inferredFromIsAgency: false,
  };
}

export function resolveEngagementAccountability(input: {
  classificationState: EngagementBootstrap['classificationState'];
  workerClassification: EngagementBootstrap['workerClassification'];
  operatingModel?: string | null;
  agencySupplierId?: string | null;
  headRegisteredProviderId?: string | null;
  settlementModel?: string | null;
  arrangementType?: string | null;
  deliveryHostTenantId?: string | null;
}): 'RESOLVED' | 'UNABLE_TO_DETERMINE' {
  if (
    input.classificationState === 'LEGACY_UNCLASSIFIED' ||
    input.workerClassification === 'UNCLASSIFIED'
  ) {
    return 'UNABLE_TO_DETERMINE';
  }
  if (input.workerClassification === 'ASSOCIATED_PROVIDER_WORKER') {
    const validOperatingModel = ['registered', 'associated', 'mixed'].includes(
      input.operatingModel ?? '',
    );
    const validSettlement =
      !input.settlementModel ||
      [
        'direct_subsidy',
        'brokered_out',
        'brokered_in',
        'private_pay',
        'ndis_registered',
        'ndis_plan_managed',
        'ndis_self_managed',
      ].includes(input.settlementModel);
    const validArrangement =
      !input.arrangementType ||
      ['deliver_on_behalf', 'labour_hire', 'direct'].includes(input.arrangementType);
    const multiPartyIncomplete =
      !validOperatingModel ||
      !validSettlement ||
      !validArrangement ||
      (!input.agencySupplierId && !input.headRegisteredProviderId) ||
      (input.arrangementType === 'labour_hire' && !input.agencySupplierId) ||
      (input.settlementModel === 'brokered_out' && !input.headRegisteredProviderId) ||
      (!input.settlementModel && !input.arrangementType);
    if (multiPartyIncomplete) return 'UNABLE_TO_DETERMINE';
  }
  if (input.operatingModel === 'associated' && !input.headRegisteredProviderId) {
    return 'UNABLE_TO_DETERMINE';
  }
  return 'RESOLVED';
}

export function engagementsOverlap(
  a: { effectiveFrom: string; effectiveTo: string | null },
  b: { effectiveFrom: string; effectiveTo: string | null },
): boolean {
  // half-open [from, to)
  const aFrom = a.effectiveFrom;
  const aTo = a.effectiveTo ?? '9999-12-31';
  const bFrom = b.effectiveFrom;
  const bTo = b.effectiveTo ?? '9999-12-31';
  return aFrom < bTo && bFrom < aTo;
}

// ---------------------------------------------------------------------------
// Requirement override resolution (deterministic)
// ---------------------------------------------------------------------------

export type RequirementDefinition = {
  requirementCode: string;
  version: number;
  tenantId: string | null; // null = platform
  authorityBasis:
    | 'STATUTORY'
    | 'REGULATOR_GUIDANCE'
    | 'PROFESSIONAL'
    | 'STATE_OR_TERRITORY'
    | 'PROVIDER_POLICY'
    | 'CONTRACTUAL'
    | 'ROLE_OPERATIONAL';
  isStatutoryFloor: boolean;
  effectiveFrom: string;
  retiredAt: string | null;
};

export function resolveRequirementDefinition(
  defs: readonly RequirementDefinition[],
  requirementCode: string,
  tenantId: string,
  asOf: string,
): RequirementDefinition | null {
  const effective = defs.filter(
    (d) =>
      d.requirementCode === requirementCode &&
      d.effectiveFrom <= asOf &&
      (d.retiredAt === null || d.retiredAt > asOf),
  );
  // Legislation-backed platform definitions are immutable floors. A tenant
  // may add a stricter, separately coded requirement, but cannot shadow the
  // same statutory code or downgrade its enforcement semantics.
  const statutoryFloor = effective
    .filter((d) => d.tenantId === null && d.authorityBasis === 'STATUTORY' && d.isStatutoryFloor)
    .sort((a, b) => b.version - a.version || b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
  if (statutoryFloor) return statutoryFloor;
  const tenant = effective
    .filter((d) => d.tenantId === tenantId)
    .sort((a, b) => b.version - a.version || b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
  if (tenant) return tenant;
  const platform = effective
    .filter((d) => d.tenantId === null)
    .sort((a, b) => b.version - a.version || b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
  return platform ?? null;
}

// ---------------------------------------------------------------------------
// Statutory worker-screening regime and decision helpers
// ---------------------------------------------------------------------------

export const AGED_CARE_PROGRAM_CODES = [
  'RAC',
  'RESIDENTIAL',
  'AN_ACC',
  'SAH',
  'SUPPORT_AT_HOME',
  'HCP',
  'CHSP',
  'NATSIFACP',
  'MPSP',
  'TCP',
  'TRANSITION_CARE',
  'STRC',
  'FLEXIBLE_CARE',
] as const;

export type AgedCareScreeningRegime = 'GENERAL_AGED_CARE' | 'CHSP_NATSIFACP' | 'UNDETERMINED';

export type AgedCareWorkerStatus = 'CONFIRMED' | 'NOT_AGED_CARE_WORKER' | 'UNDETERMINED';

/**
 * Canonical internal registration-category vocabulary. The subscription-trial
 * category is a named statutory category, not an invented numeric category 7.
 */
export const AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODES = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  'SUBSCRIPTION_TRIAL',
] as const;

export type AgedCareProviderRegistrationCategoryCode =
  (typeof AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODES)[number];

const AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODE_SET = new Set<string>(
  AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODES,
);

/**
 * Resolves the Rules 152 screening family from explicit program context.
 * Mixed delivery containing CHSP/NATSIFACP uses that distinct rule family.
 * Missing or unrecognised context never defaults to a permissive regime.
 */
export function resolveAgedCareScreeningRegime(input: {
  agedCareWorkerStatus: AgedCareWorkerStatus;
  programCodes: readonly string[];
}): AgedCareScreeningRegime {
  if (input.agedCareWorkerStatus !== 'CONFIRMED') return 'UNDETERMINED';
  const programs = new Set(
    input.programCodes.map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  if (programs.size === 0) return 'UNDETERMINED';
  if (programs.has('CHSP') || programs.has('NATSIFACP')) return 'CHSP_NATSIFACP';
  if ([...programs].some((code) => (AGED_CARE_PROGRAM_CODES as readonly string[]).includes(code))) {
    return 'GENERAL_AGED_CARE';
  }
  return 'UNDETERMINED';
}

/**
 * Responsible-person CHSP routing also depends on the provider registration
 * category (Rules 152-15(5), 152-16(6), 152-17(2)). Category codes follow the
 * registered-provider record's statutory numeric vocabulary.
 */
export function resolveResponsiblePersonScreeningRegime(input: {
  providerProgramCodes: readonly string[];
  registrationCategoryCodes: readonly string[];
}): AgedCareScreeningRegime {
  const programs = new Set(
    input.providerProgramCodes.map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  const categories = new Set(
    input.registrationCategoryCodes.map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  if (programs.size === 0) return 'UNDETERMINED';
  if (programs.has('NATSIFACP')) return 'CHSP_NATSIFACP';
  if (programs.has('CHSP')) {
    if (
      categories.size === 0 ||
      [...categories].some((code) => !AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODE_SET.has(code))
    ) {
      return 'UNDETERMINED';
    }
    return [...categories].some((code) => ['1', '2', '3', '4', '5'].includes(code))
      ? 'CHSP_NATSIFACP'
      : 'GENERAL_AGED_CARE';
  }
  return resolveAgedCareScreeningRegime({
    agedCareWorkerStatus: 'CONFIRMED',
    programCodes: [...programs],
  });
}

export type ScreeningDecisionInput = {
  regime: AgedCareScreeningRegime;
  subjectType?: 'AGED_CARE_WORKER' | 'RESPONSIBLE_PERSON';
  programCodes?: readonly string[];
  legalBasis:
    | 'POLICE_CERTIFICATE'
    | 'NDIS_CLEARANCE'
    | 'PENDING_APPLICATION_EXCEPTION'
    | 'CHSP_TRANSITIONAL_CERTIFICATE'
    | 'STUDENT_DIRECT_SUPERVISION'
    | 'NO_LAWFUL_BASIS';
  asOf: string;
  issuedAt?: string | null;
  validUntil?: string | null;
  applicationPending?: boolean;
  statutoryDeclarationProvided?: boolean;
  appropriateSupervisionRecorded?: boolean;
  directSupervisionRecorded?: boolean;
  existingEvidenceExpired?: boolean;
  banningOrderOrExclusion?: boolean;
  relevantOffence?: boolean;
  deathOffence?: boolean;
  sexualOrDishonestyOffenceWithinFiveYears?: boolean;
  chspContinuationPrecludingOffence?: boolean;
  subjectEngagedAt?: string | null;
  internationalHistoryAfterAge16?: boolean;
  internationalOffenceDeclarationProvided?: boolean;
  secondarySchoolFormalWorkExperience?: boolean;
  basisInForce?: boolean | null;
  basisCheckBusinessDate?: string | null;
  basisCheckSource?: string | null;
};

export type ScreeningDecision = {
  outcome: 'CLEARED' | 'CONDITIONAL' | 'BLOCKED' | 'UNDETERMINED';
  reasonCode: string;
};

const OFFICIAL_SCREENING_BASIS_SOURCES = new Set([
  'OFFICIAL_REGISTER',
  'ISSUING_AUTHORITY',
  'GOVERNMENT_PORTAL',
  'SOURCE_SCREENING_RECORD',
  'DOCUMENTED_OFFICIAL_RESPONSE',
]);

export function resolveScreeningAuthorityReference(input: {
  regime: Exclude<AgedCareScreeningRegime, 'UNDETERMINED'>;
  subjectType: 'AGED_CARE_WORKER' | 'RESPONSIBLE_PERSON';
  legalBasis: ScreeningDecisionInput['legalBasis'];
  programCodes?: readonly string[];
}): string {
  const isPoliceBasis =
    input.legalBasis === 'POLICE_CERTIFICATE' ||
    input.legalBasis === 'CHSP_TRANSITIONAL_CERTIFICATE';
  if (input.regime === 'GENERAL_AGED_CARE') {
    if (input.subjectType === 'AGED_CARE_WORKER') {
      return isPoliceBasis
        ? 'Aged Care Rules 2025 rr 152-20 and 152-25'
        : 'Aged Care Rules 2025 r 152-20';
    }
    return isPoliceBasis
      ? 'Aged Care Rules 2025 rr 152-15 and 152-25'
      : 'Aged Care Rules 2025 r 152-15';
  }

  const programs = new Set(
    (input.programCodes ?? []).map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  const isOtherProviderPath = [...programs].some((code) => code !== 'CHSP' && code !== 'NATSIFACP');
  const decisionRule =
    input.subjectType === 'AGED_CARE_WORKER'
      ? isOtherProviderPath
        ? '152-22'
        : '152-21'
      : isOtherProviderPath
        ? '152-16 and 152-17'
        : '152-16';
  if (decisionRule === '152-16 and 152-17' && isPoliceBasis) {
    return 'Aged Care Rules 2025 rr 152-16, 152-17 and 152-25';
  }
  return `Aged Care Rules 2025 ${
    decisionRule.includes(' and ') || isPoliceBasis ? 'rr' : 'r'
  } ${decisionRule}${isPoliceBasis ? ' and 152-25' : ''}`;
}

export type ProviderTrainingApplicability = {
  state: 'APPLICABLE' | 'NOT_APPLICABLE' | 'DEGRADED';
  reasonCode:
    | 'REGISTERED_PROVIDER_CATEGORY_APPLIES'
    | 'AT_HOME_MODIFICATION_ONLY'
    | 'REGISTERED_PROVIDER_CATEGORY_UNRESOLVED'
    | 'GENERAL_REGISTERED_PROVIDER_REQUIREMENT';
};

export function resolveProviderTrainingApplicability(input: {
  requirementCode: string;
  registrationCategoryCodes: readonly string[];
  categoryContextState: 'RESOLVED' | 'DEGRADED';
}): ProviderTrainingApplicability {
  const requirementCode = input.requirementCode.trim().toUpperCase();
  if (
    requirementCode === 'COMPLAINTS_FEEDBACK_SYSTEM_TRAINING' ||
    requirementCode === 'WHISTLEBLOWER_SYSTEM_TRAINING'
  ) {
    return {
      state: 'APPLICABLE',
      reasonCode: 'GENERAL_REGISTERED_PROVIDER_REQUIREMENT',
    };
  }

  const categories = new Set(
    input.registrationCategoryCodes.map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  const hasInvalidCategory = [...categories].some(
    (code) => !AGED_CARE_PROVIDER_REGISTRATION_CATEGORY_CODE_SET.has(code),
  );
  if (
    requirementCode !== 'INCIDENT_MANAGEMENT_SYSTEM_TRAINING' ||
    input.categoryContextState === 'DEGRADED' ||
    categories.size === 0 ||
    hasInvalidCategory
  ) {
    return {
      state: 'DEGRADED',
      reasonCode: 'REGISTERED_PROVIDER_CATEGORY_UNRESOLVED',
    };
  }
  if (categories.size === 1 && categories.has('2')) {
    return { state: 'NOT_APPLICABLE', reasonCode: 'AT_HOME_MODIFICATION_ONLY' };
  }
  return {
    state: 'APPLICABLE',
    reasonCode: 'REGISTERED_PROVIDER_CATEGORY_APPLIES',
  };
}

function addYearsIso(date: string, years: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const targetYear = Number(match[1]) + years;
  const month = Number(match[2]);
  const day = Number(match[3]);
  const lastDayOfMonth = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  return `${String(targetYear).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
    Math.min(day, lastDayOfMonth),
  ).padStart(2, '0')}`;
}

/** Pure fail-closed mirror of the database screening decision receipt. */
export function evaluateAgedCareScreeningDecision(
  input: ScreeningDecisionInput,
): ScreeningDecision {
  if (input.banningOrderOrExclusion) {
    return { outcome: 'BLOCKED', reasonCode: 'BANNING_ORDER_OR_EXCLUSION' };
  }
  if (input.regime === 'UNDETERMINED') {
    return { outcome: 'UNDETERMINED', reasonCode: 'SCREENING_REGIME_UNDETERMINED' };
  }
  if (input.legalBasis === 'NO_LAWFUL_BASIS') {
    return { outcome: 'BLOCKED', reasonCode: 'NO_LAWFUL_SCREENING_BASIS' };
  }

  const subjectType = input.subjectType ?? 'AGED_CARE_WORKER';
  const programs = new Set(
    (input.programCodes ?? []).map((code) => code.trim().toUpperCase()).filter(Boolean),
  );
  if (input.regime === 'CHSP_NATSIFACP' && !programs.has('CHSP') && !programs.has('NATSIFACP')) {
    return { outcome: 'UNDETERMINED', reasonCode: 'SCREENING_PROGRAM_CONTEXT_REQUIRED' };
  }

  const currentThroughAsOf = Boolean(input.validUntil && input.validUntil >= input.asOf);
  const certificateWithinThreeYears = Boolean(
    input.issuedAt && addYearsIso(input.issuedAt, 3) >= input.asOf,
  );
  const certificateCurrent =
    certificateWithinThreeYears && (!input.validUntil || input.validUntil >= input.asOf);

  if (input.regime === 'GENERAL_AGED_CARE' && input.relevantOffence) {
    return { outcome: 'BLOCKED', reasonCode: 'RELEVANT_OFFENCE' };
  }
  if (input.regime === 'CHSP_NATSIFACP' && input.chspContinuationPrecludingOffence) {
    return { outcome: 'BLOCKED', reasonCode: 'CHSP_PRECLUDING_OFFENCE' };
  }

  if (input.legalBasis === 'NDIS_CLEARANCE') {
    if (input.basisInForce === false) {
      return { outcome: 'BLOCKED', reasonCode: 'NDIS_CLEARANCE_NOT_IN_FORCE' };
    }
    if (input.validUntil) {
      return currentThroughAsOf
        ? { outcome: 'CLEARED', reasonCode: 'NDIS_CLEARANCE_CURRENT' }
        : { outcome: 'BLOCKED', reasonCode: 'NDIS_CLEARANCE_NOT_CURRENT' };
    }
    const source = input.basisCheckSource?.trim().toUpperCase() ?? '';
    if (
      input.basisInForce === true &&
      input.basisCheckBusinessDate === input.asOf &&
      OFFICIAL_SCREENING_BASIS_SOURCES.has(source)
    ) {
      return { outcome: 'CLEARED', reasonCode: 'NDIS_CLEARANCE_CURRENT_OFFICIAL_PROVENANCE' };
    }
    return { outcome: 'UNDETERMINED', reasonCode: 'AWAITING_VERIFICATION' };
  }
  if (input.legalBasis === 'PENDING_APPLICATION_EXCEPTION') {
    if (
      input.regime === 'CHSP_NATSIFACP' &&
      (input.deathOffence || input.sexualOrDishonestyOffenceWithinFiveYears)
    ) {
      return {
        outcome: 'BLOCKED',
        reasonCode: 'CHSP_PENDING_DECLARATION_PRECLUDING_OFFENCE',
      };
    }
    if (input.existingEvidenceExpired) {
      return { outcome: 'BLOCKED', reasonCode: 'PENDING_EXCEPTION_NOT_FOR_EXPIRED_EVIDENCE' };
    }
    return input.applicationPending &&
      input.statutoryDeclarationProvided &&
      input.appropriateSupervisionRecorded
      ? { outcome: 'CONDITIONAL', reasonCode: 'PENDING_APPLICATION_CONDITIONS_MET' }
      : { outcome: 'BLOCKED', reasonCode: 'PENDING_APPLICATION_CONDITIONS_INCOMPLETE' };
  }
  if (input.legalBasis === 'STUDENT_DIRECT_SUPERVISION') {
    if (subjectType === 'RESPONSIBLE_PERSON') {
      return { outcome: 'BLOCKED', reasonCode: 'SCREENING_BASIS_NOT_APPLICABLE' };
    }
    return input.secondarySchoolFormalWorkExperience && input.directSupervisionRecorded
      ? { outcome: 'CONDITIONAL', reasonCode: 'STUDENT_DIRECT_SUPERVISION_RECORDED' }
      : {
          outcome: 'BLOCKED',
          reasonCode: 'STUDENT_FORMAL_PLACEMENT_OR_SUPERVISION_MISSING',
        };
  }

  if (
    input.legalBasis !== 'POLICE_CERTIFICATE' &&
    input.legalBasis !== 'CHSP_TRANSITIONAL_CERTIFICATE'
  ) {
    return { outcome: 'BLOCKED', reasonCode: 'SCREENING_BASIS_NOT_APPLICABLE' };
  }
  if (!certificateCurrent) {
    return {
      outcome: 'BLOCKED',
      reasonCode:
        input.regime === 'CHSP_NATSIFACP'
          ? 'CHSP_POLICE_CERTIFICATE_NOT_CURRENT'
          : 'POLICE_CERTIFICATE_NOT_CURRENT',
    };
  }
  if (input.regime === 'GENERAL_AGED_CARE') {
    if (input.legalBasis === 'CHSP_TRANSITIONAL_CERTIFICATE') {
      return { outcome: 'BLOCKED', reasonCode: 'SCREENING_BASIS_NOT_APPLICABLE' };
    }
    if (input.internationalHistoryAfterAge16 && !input.internationalOffenceDeclarationProvided) {
      return {
        outcome: 'BLOCKED',
        reasonCode: 'INTERNATIONAL_OFFENCE_DECLARATION_REQUIRED',
      };
    }
    return { outcome: 'CLEARED', reasonCode: 'POLICE_CERTIFICATE_CURRENT' };
  }

  if (!programs.has('CHSP') && programs.has('NATSIFACP')) {
    if (input.deathOffence || input.sexualOrDishonestyOffenceWithinFiveYears) {
      return { outcome: 'BLOCKED', reasonCode: 'CHSP_PRECLUDING_OFFENCE' };
    }
    if (input.internationalHistoryAfterAge16 && !input.internationalOffenceDeclarationProvided) {
      return {
        outcome: 'BLOCKED',
        reasonCode: 'INTERNATIONAL_OFFENCE_DECLARATION_REQUIRED',
      };
    }
    return { outcome: 'CLEARED', reasonCode: 'NATSIFACP_POLICE_CERTIFICATE_CURRENT' };
  }

  const rulesCommencement = '2025-11-01';
  if (!input.subjectEngagedAt || !input.issuedAt) {
    return {
      outcome: 'BLOCKED',
      reasonCode: 'CHSP_ENGAGEMENT_AND_CERTIFICATE_DATES_REQUIRED',
    };
  }
  if (input.subjectEngagedAt < rulesCommencement && input.issuedAt < rulesCommencement) {
    return { outcome: 'CLEARED', reasonCode: 'CHSP_TRANSITIONAL_CERTIFICATE_CURRENT' };
  }
  if (input.subjectEngagedAt >= rulesCommencement || input.issuedAt >= rulesCommencement) {
    if (input.deathOffence || input.sexualOrDishonestyOffenceWithinFiveYears) {
      return { outcome: 'BLOCKED', reasonCode: 'CHSP_PRECLUDING_OFFENCE' };
    }
    if (input.internationalHistoryAfterAge16 && !input.internationalOffenceDeclarationProvided) {
      return {
        outcome: 'BLOCKED',
        reasonCode: 'INTERNATIONAL_OFFENCE_DECLARATION_REQUIRED',
      };
    }
    return { outcome: 'CLEARED', reasonCode: 'CHSP_POLICE_CERTIFICATE_CURRENT' };
  }
  return { outcome: 'BLOCKED', reasonCode: 'SCREENING_BASIS_NOT_APPLICABLE' };
}

/** Later-of creation/update plus the statutory seven-year minimum. */
export function workforceRecordRetentionFloor(input: {
  createdAt: string;
  updatedAt: string;
}): string {
  // The anchor picked here sets the statutory seven-year workforce-record retention
  // floor, so choosing the wrong one destroys or over-holds a record. `>` on ISO
  // strings is a text compare: the same instant written `…+00:00` sorts before the
  // same instant written `…Z`, so a `new Date().toISOString()` createdAt could beat
  // a strictly later timestamptz updatedAt. Compare parsed instants, and prefer the
  // parseable value so an unreadable timestamp can never become the anchor.
  const createdMs = Date.parse(input.createdAt);
  const updatedMs = Date.parse(input.updatedAt);
  const anchor =
    Number.isFinite(updatedMs) && (!Number.isFinite(createdMs) || updatedMs >= createdMs)
      ? input.updatedAt
      : input.createdAt;
  const date = new Date(anchor);
  date.setUTCFullYear(date.getUTCFullYear() + 7);
  return date.toISOString();
}

// ---------------------------------------------------------------------------
// Credential cutover pure helpers
// ---------------------------------------------------------------------------

export type CutoverOutcome =
  | 'READY'
  | 'DUPLICATE'
  | 'CONFLICT'
  | 'UNMAPPED_TYPE'
  | 'MISSING_EVIDENCE';

export const STAFF_QUALIFICATION_FIELD_MAP = {
  'staff_qualifications.id': 'worker_certifications.metadata.source_id',
  'staff_qualifications.qualification_type+name': 'worker_certifications.certification_type',
  'staff_qualifications.name': 'worker_certifications.certification_label',
  'staff_qualifications.issuing_body': 'worker_certifications.issuing_body',
  'staff_qualifications.date_obtained': 'worker_certifications.issued_at',
  'staff_qualifications.expiry_date': 'worker_certifications.expires_at',
  'staff_qualifications.status': 'conservative PENDING/EXPIRED only',
  'staff_qualifications.document_url': 'metadata.legacy_document_url (not documents.id)',
  verification_status: 'UNVERIFIED always on cutover',
} as const;

export function mapStaffQualificationType(
  qualificationType: string | null | undefined,
  name: string | null | undefined,
): string | null {
  const type = (qualificationType ?? '').toUpperCase();
  const n = (name ?? '').toLowerCase();
  if (type === 'CERTIFICATION' && n.includes('first aid')) return 'FIRST_AID';
  if (type === 'CERTIFICATION' && n.includes('cpr')) return 'CPR';
  if (type === 'CERTIFICATION' && n.includes('ahpra')) return 'AHPRA_REGISTRATION';
  if (type === 'CERTIFICATION' && n.includes('ndis')) return 'NDIS_WORKER_SCREENING';
  if (type === 'CERTIFICATION' && n.includes('police')) return 'POLICE_CHECK';
  if (type === 'CERTIFICATION' && n.includes('immun')) {
    return 'IMMUNISATION';
  }
  if (type === 'CERTIFICATION' && n.includes('driver')) return 'DRIVER_LICENCE';
  if (['TRAINING', 'MANDATORY', 'PROFESSIONAL_DEVELOPMENT'].includes(type)) return null;
  if (type === 'CERTIFICATION') return 'QUALIFICATION';
  return null;
}

export function legacyStatusToCutoverStatus(legacyStatus: string | null | undefined): {
  status: 'PENDING' | 'EXPIRED';
  verificationStatus: 'UNVERIFIED';
} {
  // Never auto-verify or promote CURRENT legacy rows to CURRENT/VERIFIED gate evidence.
  if ((legacyStatus ?? '').toUpperCase() === 'EXPIRED') {
    return { status: 'EXPIRED', verificationStatus: 'UNVERIFIED' };
  }
  return { status: 'PENDING', verificationStatus: 'UNVERIFIED' };
}

export function classifyQualificationCutoverRow(input: {
  sourceId: string;
  qualificationType: string;
  name: string;
  status: string;
  issuingBody?: string | null;
  dateObtained?: string | null;
  expiryDate?: string | null;
  documentUrl?: string | null;
  existingCanonical?: {
    sourceId?: string | null;
    issuedAt?: string | null;
    expiresAt?: string | null;
    issuingBody?: string | null;
    status?: string | null;
  } | null;
}): { outcome: CutoverOutcome; mappedType: string | null; cutoverStatus: 'PENDING' | 'EXPIRED' } {
  const mappedType = mapStaffQualificationType(input.qualificationType, input.name);
  const cutoverStatus = legacyStatusToCutoverStatus(input.status).status;
  if (!mappedType) {
    return { outcome: 'UNMAPPED_TYPE', mappedType: null, cutoverStatus };
  }
  if (!input.documentUrl && !input.issuingBody && !input.dateObtained) {
    return { outcome: 'MISSING_EVIDENCE', mappedType, cutoverStatus };
  }
  const existing = input.existingCanonical;
  if (existing) {
    if (existing.sourceId === input.sourceId) {
      return { outcome: 'DUPLICATE', mappedType, cutoverStatus };
    }
    if (
      (existing.issuedAt ?? null) !== (input.dateObtained ?? null) ||
      (existing.expiresAt ?? null) !== (input.expiryDate ?? null) ||
      (existing.issuingBody ?? '') !== (input.issuingBody ?? '')
    ) {
      return { outcome: 'CONFLICT', mappedType, cutoverStatus };
    }
    return { outcome: 'DUPLICATE', mappedType, cutoverStatus };
  }
  return { outcome: 'READY', mappedType, cutoverStatus };
}
